import { computed, nextTick, ref } from 'vue'

import type { ComputedRef, Ref } from 'vue'
import type { PluralForms } from '@shared/lib/format'

import { withCount } from '@shared/lib/format'

import { countDragGaps, countGapTokens } from '../../lib/assessments/schema'

/**
 * The two marker flavours. Both wrap the answer; they differ in what the kid does with it:
 *
 *  - `gap`  — `fill_in_blanks`, `{{answer}}` (`GAP_TOKEN_REGEX`). The kid types it. Stored as bare
 *             `{{}}` plus a derived `blanks` array; the author never sees that form.
 *  - `drag` — `drag_the_words`, `*answer*` (`DRAG_GAP_REGEX`). The kid drags it from a bank.
 *             Stored exactly as authored.
 *
 * An empty `gap` marker is legal; an empty `drag` marker is not a marker at all, which is why only
 * `drag` inserts a placeholder word.
 */
type AnnotatedTextareaMarker = 'gap' | 'drag'

export interface AnnotatedTextareaProps {
  value?: string
  label?: string
  placeholder?: string
  hint?: string
  disabled?: boolean
  required?: boolean
  /** Which insert buttons this instance offers. Empty (the default) means none — a plain text
   *  block declares no gaps, so it gets a bare textarea with no insert row and no counter. */
  markers?: AnnotatedTextareaMarker[]
  /** Which flavour the counter reports. Defaults to the first entry of `markers`; `null` (or an
   *  empty `markers`) means no counter. Spelled separately so a counter without an insert button
   *  stays possible. */
  countAs?: AnnotatedTextareaMarker | null
}

// No `expectedCount`: both flavours now carry their answer inside the marker, so nothing can
// disagree with the gap count and the counter alone is the useful signal.

/** The result of inserting a marker: the new text plus where the caret should end up. */
interface AnnotatedTextareaInsertion {
  value: string
  selectionStart: number
  selectionEnd: number
}

interface UseAnnotatedTextareaOptions {
  props: AnnotatedTextareaProps
  onUpdateValue: (value: string) => void
}

interface UseAnnotatedTextareaReturn {
  /** Attach to the component root — the caret lives on the `<textarea>` inside `AppTextarea`,
   *  which does not expose its element. */
  refRoot: Ref<HTMLElement | null>
  refValue: ComputedRef<string>
  refMarkers: ComputedRef<AnnotatedTextareaMarker[]>
  refCountAs: ComputedRef<AnnotatedTextareaMarker | null>
  refCount: ComputedRef<number | null>
  refCounterText: ComputedRef<string | null>
  insertMarker: (marker: AnnotatedTextareaMarker) => void
  handleInput: (value: string) => void
  getMarkerLabel: (marker: AnnotatedTextareaMarker) => string
}

/**
 * The word a marker wraps when the caret is a bare insertion point, per flavour.
 *
 * `drag` needs one because `*_*` with nothing between the asterisks is not a marker at all. `gap`
 * needs one because `{{}}` is a legal gap but four characters of punctuation are invisible in a
 * sentence, while the button promises "Insert gap {{answer}}". Both insert a visible word and
 * leave it SELECTED, so the first keystroke replaces it.
 */
const MARKER_PLACEHOLDERS: Record<AnnotatedTextareaMarker, string> = {
  gap: 'answer',
  drag: 'word',
}

/** Deliberately parallel wording: both markers wrap the answer, and the label shows the shape. */
const MARKER_LABELS: Record<AnnotatedTextareaMarker, string> = {
  gap: 'Insert gap {{answer}}',
  drag: 'Insert *answer*',
}

const COUNTER_NOUNS: Record<AnnotatedTextareaMarker, PluralForms> = {
  gap: { one: 'gap', other: 'gaps' },
  drag: { one: 'draggable word', other: 'draggable words' },
}

/** `countGapTokens`, not `countGapMarkers`: this value is the INLINE authored text, where a gap
 *  carries its answer between the braces. */
const countFor = (marker: AnnotatedTextareaMarker, text: string): number =>
  marker === 'gap' ? countGapTokens(text) : countDragGaps(text)

/**
 * Insert a marker AT THE CARET (§3f) — appending at the end would be useless, since the job is
 * marking a gap mid-sentence. Pure, so the caret arithmetic is testable without a DOM.
 *
 * Both flavours wrap the selection — or the flavour's placeholder word — and leave the inside of
 * the marker selected, so the next keystroke types the answer over it: `{{answer}}` / `*word*`.
 */
export const insertMarkerAtCaret = (
  value: string,
  selectionStart: number,
  selectionEnd: number,
  marker: AnnotatedTextareaMarker,
): AnnotatedTextareaInsertion => {
  const start = Math.max(0, Math.min(selectionStart, value.length))
  const end = Math.max(start, Math.min(selectionEnd, value.length))
  const before = value.slice(0, start)
  const after = value.slice(end)
  const selected = value.slice(start, end)
  const inner = end > start ? selected : MARKER_PLACEHOLDERS[marker]

  const [open, close] = marker === 'gap' ? ['{{', '}}'] : ['*', '*']

  return {
    value: `${before}${open}${inner}${close}${after}`,
    selectionStart: start + open.length,
    selectionEnd: start + open.length + inner.length,
  }
}

export const useAnnotatedTextarea = (
  { props, onUpdateValue }: UseAnnotatedTextareaOptions,
): UseAnnotatedTextareaReturn => {
  const refRoot = ref<HTMLElement | null>(null)

  const refValue = computed<string>(() => props.value ?? '')

  const refMarkers = computed<AnnotatedTextareaMarker[]>(() => props.markers ?? [])

  const refCountAs = computed<AnnotatedTextareaMarker | null>(() => {
    if (props.countAs !== undefined) {
      return props.countAs
    }
    return refMarkers.value[0] ?? null
  })

  const refCount = computed<number | null>(() =>
    refCountAs.value ? countFor(refCountAs.value, refValue.value) : null,
  )

  const refCounterText = computed<string | null>(() => {
    const marker = refCountAs.value
    if (!marker || refCount.value === null) {
      return null
    }
    return withCount(refCount.value, COUNTER_NOUNS[marker])
  })

  const getTextarea = (): HTMLTextAreaElement | null =>
    refRoot.value?.querySelector('textarea') ?? null

  const insertMarker = (marker: AnnotatedTextareaMarker): void => {
    const textarea = getTextarea()
    // No element (not mounted, or the kit changed shape) — do nothing rather than append at the
    // end, which is the behaviour §3f forbids.
    if (!textarea) {
      return
    }

    const insertion = insertMarkerAtCaret(
      refValue.value,
      textarea.selectionStart ?? refValue.value.length,
      textarea.selectionEnd ?? refValue.value.length,
      marker,
    )

    onUpdateValue(insertion.value)

    // The value round-trips through the owner, so the caret is restored after the prop comes back
    // down; otherwise re-rendering drops it to the end.
    void nextTick(() => {
      const element = getTextarea()
      if (!element) {
        return
      }
      element.focus()
      element.setSelectionRange(insertion.selectionStart, insertion.selectionEnd)
    })
  }

  const handleInput = (value: string): void => {
    onUpdateValue(value)
  }

  const getMarkerLabel = (marker: AnnotatedTextareaMarker): string => MARKER_LABELS[marker]

  return {
    refRoot,
    refValue,
    refMarkers,
    refCountAs,
    refCount,
    refCounterText,
    insertMarker,
    handleInput,
    getMarkerLabel,
  }
}
